import { BattleEngine, ENGINE_VERSION } from '../../engine/engine';
import type { BattleView, CardDefinition, ContentSnapshot, PlayerId } from '../../engine/types';
import type { ReplayFile } from '../../replay/replay';
import { compareStableText } from '../../engine/stable-order';
import type { PlayerMatchFeatures, PlayerStyleVector } from './types';

const clampStyle = (value: number): number => Math.max(0, Math.min(1_000, Math.round(value)));

function cardHasAirUnit(card: CardDefinition, content: ContentSnapshot): boolean {
  return card.mechanics.some((mechanic) => mechanic.type === 'spawn-units' && mechanic.groups.some((group) => content.units.get(group.unitId)?.movement === 'air'));
}

interface ActionSample {
  readonly cost: number;
  readonly forwardness: number;
  readonly lane: number;
  readonly defensive: boolean;
  readonly tank: boolean;
  readonly air: boolean;
  readonly swarm: boolean;
  readonly spell: boolean;
  readonly supportAfterTank: boolean;
}

function sampleAction(view: BattleView, card: CardDefinition, position: { x: number; y: number }, content: ContentSnapshot, lastTankTick: number | null): ActionSample {
  const bounds = view.arena.worldBounds;
  const forwardness = view.player.id === 'player'
    ? (bounds.y + bounds.height - position.y) / bounds.height
    : (position.y - bounds.y) / bounds.height;
  const centerX = bounds.x + bounds.width / 2;
  const lane = Math.max(-1, Math.min(1, (position.x - centerX) / (bounds.width / 2)));
  const centerY = bounds.y + bounds.height / 2;
  const threats = view.entities.filter((entity) => entity.owner !== view.player.id && entity.kind !== 'tower' && !entity.dead && (view.player.id === 'player' ? entity.y > centerY : entity.y < centerY));
  const defensive = card.roles.includes('defense') || threats.length > 0;
  return {
    cost: card.cost,
    forwardness: Math.max(0, Math.min(1, forwardness)),
    lane,
    defensive,
    tank: card.roles.includes('tank'),
    air: cardHasAirUnit(card, content),
    swarm: card.roles.includes('swarm'),
    spell: card.roles.includes('spell'),
    supportAfterTank: !card.roles.includes('tank') && lastTankTick !== null && view.tick - lastTankTick <= 180,
  };
}

function styleFromSamples(samples: readonly ActionSample[], durationTicks: number, tickRate: number): { style: PlayerStyleVector; averageCostMilli: number } {
  if (!samples.length) return {
    style: { aggression: 0, cycle: 0, beatdown: 0, airPressure: 0, swarm: 0, spellControl: 0, defense: 0, lanePreference: 0 },
    averageCostMilli: 0,
  };
  const ratio = (predicate: (sample: ActionSample) => boolean): number => samples.filter(predicate).length / samples.length;
  const averageCost = samples.reduce((sum, sample) => sum + sample.cost, 0) / samples.length;
  const minutes = Math.max(0.25, durationTicks / tickRate / 60);
  const playsPerMinute = samples.length / minutes;
  const averageForwardness = samples.reduce((sum, sample) => sum + sample.forwardness, 0) / samples.length;
  const averageLane = samples.reduce((sum, sample) => sum + sample.lane, 0) / samples.length;
  return {
    averageCostMilli: Math.round(averageCost * 1_000),
    style: {
      aggression: clampStyle(averageForwardness * 1_000),
      cycle: clampStyle((4.5 - averageCost) * 180 + playsPerMinute * 24),
      beatdown: clampStyle(ratio((sample) => sample.tank) * 650 + ratio((sample) => sample.supportAfterTank) * 500),
      airPressure: clampStyle(ratio((sample) => sample.air) * 1_400),
      swarm: clampStyle(ratio((sample) => sample.swarm) * 1_300),
      spellControl: clampStyle(ratio((sample) => sample.spell) * 1_500),
      defense: clampStyle(ratio((sample) => sample.defensive) * 1_000),
      lanePreference: Math.max(-1_000, Math.min(1_000, Math.round(averageLane * 1_000))),
    },
  };
}

export function extractPlayerMatchFeatures(matchId: string, replay: ReplayFile, content: ContentSnapshot, playerId: PlayerId = 'player'): PlayerMatchFeatures {
  if (replay.engineVersion !== ENGINE_VERSION || replay.contentVersion !== content.version) throw new Error('Learning requires an exact current-version replay');
  if (!replay.result) throw new Error('Learning requires a completed replay');
  const engine = new BattleEngine({
    seed: replay.seed,
    content,
    arenaId: replay.arenaId,
    decks: replay.decks,
    rules: replay.rules,
    diagnosticProfile: 'off',
    matchId: `learning-${matchId}`,
    ...(replay.botStrategy ? { botStrategy: replay.botStrategy } : {}),
  });
  const commands = [...replay.commands].sort((left, right) => left.tick - right.tick || compareStableText(left.playerId, right.playerId) || left.sequence - right.sequence);
  const samples: ActionSample[] = [];
  let commandIndex = 0;
  let lastTankTick: number | null = null;
  while (!engine.hasEnded && engine.currentTick <= replay.result.tick) {
    while (commands[commandIndex]?.tick === engine.currentTick) {
      const command = commands[commandIndex]!;
      if (command.playerId === playerId) {
        const card = content.cards.get(command.cardId);
        if (card) {
          const view = engine.getView(playerId);
          samples.push(sampleAction(view, card, command.position, content, lastTankTick));
          if (card.roles.includes('tank')) lastTankTick = view.tick;
        }
      }
      const result = engine.enqueue(command);
      if (!result.ok) throw new Error(`Learning replay rejected command ${command.playerId}:${command.sequence}`);
      commandIndex += 1;
    }
    engine.step();
  }
  if (engine.checksum() !== replay.checkpoints.at(-1)?.checksum) throw new Error('Learning replay checksum mismatch');
  const summary = styleFromSamples(samples, replay.result.tick, replay.rules.tickRate);
  return {
    matchId,
    style: summary.style,
    plays: samples.length,
    averageCostMilli: summary.averageCostMilli,
    playerDeck: [...replay.decks[playerId]],
    botDeck: [...replay.decks[playerId === 'player' ? 'bot' : 'player']],
    result: structuredClone(replay.result),
  };
}
